import React from 'react';
import { SignaturePad } from '@ark-ui/react/signature-pad';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface SignaturePadInputProps extends BaseInputProps<string[]> {
  /** Lokalizovaný popisek mazacího tlačítka. */
  clearLabel?: string;
  className?: string;
}

/**
 * Atomické podpisové pole postavené na Ark UI `SignaturePad`.
 *
 * RHF drží hodnotu jako pole SVG cest (`string[]`) — serializovatelné a přímo
 * znovu-vykreslitelné (controlled `paths`). Obraz podpisu lze kdykoli získat
 * přes Ark `getDataUrl()` v `onDrawEnd`, pokud konzument potřebuje PNG.
 * `ids.label` pojmenuje plochu popiskem z `<FormField />`.
 */
export const SignaturePadInput: React.FC<SignaturePadInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  required,
  describedBy,
  labelId,
  testAttributes,
  clearLabel = 'Smazat podpis',
  className,
}) => (
  <SignaturePad.Root
    name={name}
    ids={{ hiddenInput: id, label: labelId }}
    paths={value ?? []}
    disabled={disabled}
    readOnly={readOnly}
    required={required}
    onDrawEnd={(details) => {
      onChange(details.paths);
      // Dokončení tahu = interakce → touched (plocha sama blur negeneruje).
      onBlur();
    }}
    className={clsx(styles.signatureRoot, className)}
    data-invalid={hasError ? '' : undefined}
  >
    <SignaturePad.Control className={styles.signatureControl}>
      <SignaturePad.Segment className={styles.signatureSegment} />
      <SignaturePad.Guide className={styles.signatureGuide} />
      <SignaturePad.ClearTrigger
        className={styles.signatureClear}
        aria-label={clearLabel}
      >
        <Icon name="x-mark" size={16} />
      </SignaturePad.ClearTrigger>
    </SignaturePad.Control>
    <SignaturePad.HiddenInput
      ref={ref}
      // Nativní hodnota skrytého inputu zrcadlí serializovaný stav (RHF drží
      // skutečnou hodnotu jako `paths`); `value` je u Ark HiddenInput povinné.
      value={testAttributes['data-value']}
      aria-describedby={describedBy}
      {...testAttributes}
    />
  </SignaturePad.Root>
);

SignaturePadInput.displayName = 'SignaturePadInput';
